import cx from 'classnames'

import {
  type SanityProductOption,
  type SanityProductOptionSetting,
  type SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { getVariantFromOptions } from '@lib/product'

import RadioGroup from '@components/radio-group'
import ProductOptionItem from './product-option-item'

interface ProductOptionProps {
  label?: string
  option: SanityProductOption
  optionSettings: SanityProductOptionSetting[]
  variants: SanityProductVariantFragment[]
  activeVariant: SanityProductVariantFragment
  onChange: (variant: SanityProductVariantFragment) => void
  onPreview?: (variant?: SanityProductVariantFragment) => void
  strictMatch?: boolean
  hideLabels?: boolean
  inProductCard?: boolean
}

const ProductOption = ({
  label,
  option,
  optionSettings,
  variants,
  activeVariant,
  inProductCard,
  onChange,
  onPreview,
  strictMatch = true,
  hideLabels = false,
}: ProductOptionProps) => {
  const selectedValue =
    activeVariant.options.find(
      (variantOption) => variantOption.name === option.name,
    )?.value ?? ''

  const handleChange = (value: string) => {
    const newVariant = getVariantFromOptions(
      variants,
      activeVariant.options,
      option.name,
      value,
    )

    if (newVariant && newVariant.variantID !== activeVariant.variantID) {
      onChange(newVariant)
    }
  }

  return (
    <div
      className={cx('flex', {
        'mt-2 py-2 items-center': !inProductCard,
        'm-0 p-0 border-0': inProductCard,
      })}
    >
      {!hideLabels && (
        <div className="mr-12 text-xs font-semibold uppercase tracking-wider">
          {label || option.name}
        </div>
      )}

      <RadioGroup
        value={selectedValue}
        onChange={handleChange}
        className={cx('flex flex-wrap -mt-1', {
          'justify-center': inProductCard,
          'ml-auto justify-end': !inProductCard,
        })}
      >
        {option.values.map((value) => {
          if (!value) {
            return
          }

          const isActive =
            !!selectedValue &&
            activeVariant.options.some(
              (variantOption) =>
                variantOption.position === option.position &&
                variantOption.value === value,
            )

          return (
            <ProductOptionItem
              key={value}
              value={value}
              option={option}
              optionSettings={optionSettings}
              variants={variants}
              activeVariant={activeVariant}
              strictMatch={strictMatch}
              onPreview={inProductCard ? onPreview : undefined}
              isActive={isActive}
            />
          )
        })}
      </RadioGroup>
    </div>
  )
}

export default ProductOption
